<script setup>
import { ref, computed } from "vue";
import { useConsentStatus } from "@/composables/useConsentStatus";

const { isFormDisabled } = useConsentStatus();

const props = defineProps({
  surveyData: {
    type: Object,
    required: true,
  },
  currentStage: Number,
  totalStages: Number,
  nextStageInfo: Object,
});

const emit = defineEmits(["next-stage", "skip-stage"]);

const roleTypes = [
  "Technical Specialist (hands-on security implementation)",
  "Security Analyst/Researcher",
  "Security Architect/Engineer",
  "Incident Response Specialist",
  "Risk/Compliance Specialist",
  "Security Manager/Team Lead",
  "CISO/Security Director",
  "Consultant/Advisory",
  "Academic/Researcher",
  "Sales/Business Development (security-focused)",
  "Other (specify)",
];

const seniorityLevels = [
  "Entry Level (0-2 years experience)",
  "Junior (2-5 years experience)",
  "Mid-Level (5-10 years experience)",
  "Senior (10-15 years experience)",
  "Executive/C-Level",
];

const organizationTypes = [
  "Large Enterprise (1000+ employees)",
  "Medium Business (100-999 employees)",
  "Small Business (10-99 employees)",
  "Startup (less than 10 employees)",
  "Government/Public Sector",
  "Non-Profit Organization",
  "Academic Institution",
  "Consulting Firm",
  "Security Vendor/Provider",
  "Self-Employed/Freelancer",
];

const industrySectors = [
  "Financial Services",
  "Technology",
  "Healthcare",
  "Government",
  "Education",
  "Manufacturing",
  "Retail",
  "Energy",
  "Telecommunications",
  "Transportation",
  "Other",
];

const handleNext = () => {
  // Validate "Other" fields are filled when selected
  const validationErrors = [];

  // Check role type
  if (
    props.surveyData.currentRole === "Other (specify)" &&
    (!props.surveyData.otherRoleType ||
      props.surveyData.otherRoleType.trim() === "")
  ) {
    validationErrors.push(
      'Please specify your role type when "Other (specify)" is selected.'
    );
  }

  // Check industry sector
  if (
    props.surveyData.industrySector === "Other" &&
    (!props.surveyData.otherIndustrySector ||
      props.surveyData.otherIndustrySector.trim() === "")
  ) {
    validationErrors.push(
      'Please specify your industry sector when "Other" is selected.'
    );
  }

  if (validationErrors.length > 0) {
    alert(validationErrors.join("\n"));
    return;
  }

  emit("next-stage");
};
</script>

<template>
  <div class="survey-form">
    <img
      src="@/assets/cyberireland_logo.png"
      alt="Cyber Ireland Logo"
      class="logo"
      style="width: 300px; margin: 0 auto; display: block"
    />
    <h2>Professional Experience & Career Development</h2>

    <!-- Experience Metrics -->
    <div class="form-group">
      <label for="cyberExperience"
        >How many years of cybersecurity experience do you have?</label
      >
      <input
        type="number"
        id="cyberExperience"
        v-model="surveyData.cyberExperience"
        min="0"
        max="50"
        placeholder="Enter number of years (e.g., 5)"
        required
        :disabled="isFormDisabled"
      />
    </div>

    <div class="form-group">
      <label for="totalExperience"
        >How many years of total professional experience do you have (including
        non-cybersecurity)?</label
      >
      <input
        type="number"
        id="totalExperience"
        v-model="surveyData.totalExperience"
        min="0"
        max="50"
        placeholder="Enter number of years (e.g., 8)"
        required
        :disabled="isFormDisabled"
      />
    </div>

    <!-- Current Role Classification -->
    <div class="form-group">
      <label for="currentRole"
        >Which best describes your current primary role type?</label
      >
      <select
        id="currentRole"
        v-model="surveyData.currentRole"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select role type</option>
        <option v-for="role in roleTypes" :key="role" :value="role">
          {{ role }}
        </option>
      </select>
      <div
        class="form-group"
        v-if="surveyData.currentRole === 'Other (specify)'"
      >
        <input
          type="text"
          id="otherRoleType"
          v-model="surveyData.otherRoleType"
          placeholder="Enter your specific role type"
          required
          :disabled="isFormDisabled"
        />
      </div>
    </div>

    <!-- Seniority Level -->
    <div class="form-group">
      <label for="seniorityLevel">What is your current seniority level?</label>
      <select
        id="seniorityLevel"
        v-model="surveyData.seniorityLevel"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select seniority level</option>
        <option v-for="level in seniorityLevels" :key="level" :value="level">
          {{ level }}
        </option>
      </select>
    </div>

    <!-- Organization Context -->
    <div class="form-group">
      <label for="organizationType"
        >What type of organization do you primarily work for?</label
      >
      <select
        id="organizationType"
        v-model="surveyData.organizationType"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select organization type</option>
        <option v-for="type in organizationTypes" :key="type" :value="type">
          {{ type }}
        </option>
      </select>
    </div>

    <div class="form-group">
      <label for="industrySector"
        >What industry sector does your organization primarily operate
        in?</label
      >
      <select
        id="industrySector"
        v-model="surveyData.industrySector"
        required
        :disabled="isFormDisabled"
      >
        <option value="" disabled>Select industry sector</option>
        <option v-for="sector in industrySectors" :key="sector" :value="sector">
          {{ sector }}
        </option>
      </select>
      <div class="form-group" v-if="surveyData.industrySector === 'Other'">
        <input
          type="text"
          id="otherIndustrySector"
          v-model="surveyData.otherIndustrySector"
          placeholder="Enter your specific industry sector"
          required
          :disabled="isFormDisabled"
        />
      </div>
    </div>

    <!-- Navigation Buttons -->
    <div class="button-group">
      <button
        @click="handleNext"
        class="primary-button"
        :disabled="isFormDisabled"
      >
        Next
      </button>
      <button
        @click="$emit('skip-stage')"
        class="skip-button"
        :disabled="isFormDisabled"
      >
        Skip this section
      </button>
    </div>
  </div>
</template>

<style scoped>
.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: var(--text-color);
  font-size: 1rem;
  line-height: 1.4;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(66, 184, 131, 0.2);
}

.form-group select {
  cursor: pointer;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.radio-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-weight: normal;
  cursor: pointer;
  padding: 0.5rem 0;
  min-height: 44px; /* Touch-friendly height */
  line-height: 1.4;
}

.radio-label input[type="radio"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.button-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

.primary-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.primary-button:hover {
  background-color: var(--primary-hover);
}

.skip-button {
  background-color: transparent;
  color: var(--text-color);
  border: 1px solid var(--border-color);
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 48px; /* Touch-friendly height */
  width: 100%;
}

.skip-button:hover {
  background-color: var(--surface-color);
  border-color: var(--text-color);
}

/* Mobile-specific improvements */
@media (max-width: 768px) {
  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-group label {
    font-size: 0.95rem;
  }

  .form-group input,
  .form-group select {
    padding: 0.75rem;
    font-size: 0.95rem;
    min-height: 44px;
  }

  .radio-label {
    min-height: 40px;
    font-size: 0.9rem;
    gap: 0.5rem;
  }

  .radio-label input[type="radio"] {
    width: 18px;
    height: 18px;
  }

  .button-group {
    margin-top: 1.5rem;
    gap: 0.75rem;
  }

  .primary-button,
  .skip-button {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
    min-height: 44px;
  }
}

@media (max-width: 480px) {
  .form-group {
    margin-bottom: 1rem;
  }

  .form-group label {
    font-size: 0.9rem;
  }

  .form-group input,
  .form-group select {
    padding: 0.625rem;
    font-size: 0.9rem;
    min-height: 40px;
  }

  .radio-label {
    min-height: 36px;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  .radio-label input[type="radio"] {
    width: 16px;
    height: 16px;
  }

  .button-group {
    margin-top: 1.25rem;
    gap: 0.625rem;
  }

  .primary-button,
  .skip-button {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
    min-height: 40px;
  }
}
</style>
